// /var/www/afra/192.testproject.work/maracar8/0.8.2/myapp/src/components/Header.js

import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menubar } from 'primereact/menubar'; // Import Menubar
import { OverlayPanel } from 'primereact/overlaypanel';
import { Badge } from 'primereact/badge';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ToastNotification from './ToastNotification';
import { ProgressSpinner } from 'primereact/progressspinner'; // NEW! for spinner
import { useDispatch } from 'react-redux';
import { requestClearFilters } from '../redux/slices/filtersSlice';

import menuItemsData from '../data/menu/MenuItems.json'; // Import MenuItems.json
import permissionsData from '../data/menu/PermissionsMenu.json'; // Import PermissionsMenu.json

import {
  faCircleLeft, faRotateRight,
  faTachometerAlt, faCreditCard, faCalendarCheck,
  faUsers, faSpa, faSignOutAlt, faFileInvoice
} from '@fortawesome/free-solid-svg-icons';

import '../css/Header.scss';
import { API_URLS, getSelectedLanguage, setSelectedLanguage } from '../api/api';  // get/set language
import { languageOptions, languageManager } from '../utils/loadLanguage';

const Header = ({ onBack, searchQuery, setSearchQuery, placeholderText, refreshData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const op = useRef(null);

  const [bookingNotifications, setBookingNotifications] = useState([]);
  const token = localStorage.getItem('token');
  const [notificationCount, setNotificationCount] = useState(0);
  const [userOptions, setUserOptions] = useState([]);
  const userId = localStorage.getItem('user_ID');
  const toast = useRef(null);

  const [language, setLanguageState] = useState(getSelectedLanguage());
  const [loadingLanguage, setLoadingLanguage] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('primary');

  // ฟังก์ชันกลางสำหรับแจ้งเตือนด้วย ToastNotification
  const showNotify = (variant, message) => {
    setToastVariant(variant);
    setToastMessage(message);
    setShowToast(true);
  };

  // NEW! ฟังก์ชันถอดรหัส Base64 -> UTF-8
  const decodeUTF8 = (value) => {
    try {
      return decodeURIComponent(atob(value || ''));
    } catch (error) {
      console.error('Error decoding UTF-8:', error);
      return 'N/A';
    }
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoadingLanguage(true);
        await languageManager.loadLanguage(language);
        if (isMounted) {
          const langName = languageManager.translations?.langName || language;
          const Switchlang = languageManager.translations?.Switchlang || language;
          showNotify('info', `${Switchlang} ${langName}`);
        }
      } catch (err) {
        console.error('Error loading language:', err);
      } finally {
        if (isMounted) {
          setLoadingLanguage(false);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [language]);

  const handleLanguageChange = (e) => {
    setLanguageState(e.value);
    setSelectedLanguage(e.value);
    if (typeof refreshData === "function") {
      refreshData();
    }
    window.location.reload();
  };

  const languageDropdownTemplate = (option) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img
          src={option.icon}
          alt={option.label}
          style={{ width: '20px', height: '20px', marginRight: '0.5rem' }}
        />
        <span>{option.label}</span>
      </div>
    );
  };

  // ---- decode user_ID, position จาก localStorage maracarData ----
  const maracarData = JSON.parse(localStorage.getItem('maracar') || '[]')[0] || {};
  const userID = maracarData.user_ID ? decodeUTF8(maracarData.user_ID) : 'N/A';
  const position = maracarData.position ? decodeUTF8(maracarData.position) : '';

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  useEffect(() => {
    fetchUsers();
    fetchBookingNotifications();
    // eslint-disable-next-line
  }, [token]);

  const fetchBookingNotifications = async () => {
    try {
      const response = await axios.get(API_URLS.BOOKING_NOTIFICATIONS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(response.data)) {
        const formattedData = response.data.map(booking => ({
          ...booking,
          ServiceDate: new Date(booking.ServiceDate).toLocaleDateString('en-GB'),
          ServiceTime: booking.ServiceTime.substr(0, 5),
          CombinedService: {
            ServiceName: booking.ServiceName,
            PriceType: booking.PriceType
          }
        }));
        setBookingNotifications(formattedData);
        setNotificationCount(formattedData.length);
      } else {
        console.warn('Unexpected data format:', response.data);
        setBookingNotifications([]);
      }
    } catch (error) {
      console.error('Failed to fetch booking notifications:', error);
      setBookingNotifications([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(API_URLS.ALL_USERS(userId, '', '2'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        const options = response.data.map(user => ({
          label: `${user.first_name} ${user.sur_name}`,
          value: user.user_ID,
        }));
        setUserOptions(options);
      } else {
        console.warn('No data or unexpected format:', response.data);
        setUserOptions([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUserOptions([]);
    }
  };

  const employeeDropdownTemplate = (rowData) => {
    return (
      <Dropdown
        value={rowData.EmployeeID}
        options={userOptions}
        onChange={(e) => handleEmployeeChange(e.value, rowData.EmployeeID, rowData)}
        placeholder="Select Emp."
        optionLabel="label"
      />
    );
  };

  const customerTemplate = (rowData) => {
    return `${rowData.CustomerFirstName} ${rowData.CustomerSurName}, ${rowData.CustomerTelephone}`;
  };

  const dateTimeTemplate = (rowData) => {
    return `${new Date(rowData.ServiceDate).toLocaleDateString('en-GB')} at ${rowData.ServiceTime.substr(0, 5)}`;
  };

  const handleEmployeeChange = async (newEmployeeId, selectedUserId, rowData) => {
    try {
      const response = await axios.put(
        API_URLS.UPDATE_EMPLOYEE(rowData.BookingDetailID),
        { status_ID: newEmployeeId, user_ID: selectedUserId },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (response.status === 200) {
        showNotify('success', 'The employee has been updated successfully.');
        fetchBookingNotifications();
        if (refreshData) refreshData();
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      showNotify('error', 'Failed to update employee. Please try again later.');
    }
  };

  const combinedServiceTemplate = (rowData) => {
    return (
      <div>
        <div>{rowData.CombinedService.ServiceName}</div>
        <div>{rowData.CombinedService.PriceType}</div>
      </div>
    );
  };

  const handleNotificationClick = (e) => {
    op.current.toggle(e);
    setTimeout(() => {
      const panel = document.querySelector('.p-overlaypanel');
      if (panel) panel.focus();
    }, 0);
  };

  const handleRefreshData = () => {
    if (location.pathname.includes('report/booking')) {
      dispatch(requestClearFilters());
    } else if (refreshData) {
      refreshData();
    }
  };

  // ---------------------------------------------------------------------
  // SECTION: สร้างเมนู Header ตาม permissions
  // ---------------------------------------------------------------------
  const { baseMenu, contentMenu } = menuItemsData;
  const allMenus = [...baseMenu, contentMenu];

  // permissionsData.headerPermissions แยกเป็น { specialUserIDs, roles, default }
  const { headerPermissions } = permissionsData;
  const { specialUserIDs, roles, default: defaultPermissions } = headerPermissions;

  // ฟังก์ชันค้นหาเมนูตาม index
  const findMenuByIndex = (menus, targetIndexes) => {
    const menuMap = new Map();
    menus.forEach((menu) => {
      if (targetIndexes.includes(menu.index)) {
        menuMap.set(menu.index, { ...menu, subItems: menu.subItems ? [...menu.subItems] : undefined });
      }
      if (menu.subItems) {
        const matchedSubItems = menu.subItems.filter((sub) => targetIndexes.includes(sub.index));
        if (matchedSubItems.length > 0) {
          if (menuMap.has(menu.index)) {
            menuMap.get(menu.index).subItems = matchedSubItems;
          } else {
            menuMap.set(menu.index, { ...menu, subItems: matchedSubItems });
          }
        }
      }
    });
    return [...menuMap.values()];
  };

  const getHeaderMenuItems = () => {
    let allowedIndexes = [];
    if (specialUserIDs[userID]) {
      allowedIndexes = specialUserIDs[userID];
    } else if (roles[position]) {
      allowedIndexes = roles[position];
    } else {
      allowedIndexes = defaultPermissions;
    }

    // ใช้ findMenuByIndex เพื่อดึงเมนูตรง allowedIndexes
    const headerItems = findMenuByIndex(allMenus, allowedIndexes).map(item => ({
      key: item.key,
      // mapping icon ด้วย dictionary (หรือจะเก็บใน JSON ก็ได้)
      icon: {
        'dashboard': faTachometerAlt,
        'product': faSpa,
        'order': faCalendarCheck,
        'payment': faCreditCard,
        'reports': faFileInvoice,
        'staff': faUsers,
        'logout': faSignOutAlt
      }[item.key],
      command: () => navigate(item.path),
      items: item.subItems ? item.subItems.map(sub => ({
        key: sub.key,
        command: () => navigate(sub.path)
      })) : undefined
    }));

    return headerItems;
  };

  // แปลงเป็นโครงสร้างที่ Menubar เข้าใจ
  const getMenubarItems = () => {
    const menuItems = getHeaderMenuItems();

    return menuItems.map(item => {
      if (item.items) {
        return {
          label: languageManager.getMenuLabel(item.key, item.key),
          icon: <FontAwesomeIcon icon={item.icon} className="p-menuitem-icon" />,
          items: item.items.map(sub => ({
            label: languageManager.getMenuLabel(sub.key, sub.key),
            command: sub.command
          }))
        };
      } else {
        return {
          label: languageManager.getMenuLabel(item.key, item.key),
          icon: <FontAwesomeIcon icon={item.icon} className="p-menuitem-icon" />,
          command: item.command
        };
      }
    });
  };

  // ---------------------------------------------------------------------

  return (
    <>
      <ToastNotification
        showToast={showToast}
        toastMessage={toastMessage}
        toastVariant={toastVariant}
        setShowToast={setShowToast}
      />

      {loadingLanguage && (
        <div className="spinner-overlay">
          <ProgressSpinner />
        </div>
      )}

      <header className="header">
        <FontAwesomeIcon
          className="fas back_button"
          onClick={handleBack}
          icon={faCircleLeft}
        />
        <input
          type="text"
          placeholder={languageManager.getSearchPlaceholder()}
          className="search-bar"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="header-icons">
          <FontAwesomeIcon
            className="fas"
            icon={faRotateRight}
            onClick={handleRefreshData}
          />

          <i className="fas fa-bell p-overlay-badge" onClick={handleNotificationClick}>
            <Badge value={notificationCount} severity="danger"></Badge>
          </i>

          <OverlayPanel ref={op} id="overlay_panel" style={{ width: '470px' }} className="overlay-panel-scroll">
            <DataTable
              value={bookingNotifications}
              size="small"
              className="custom-small-datatable p-datatable-sm"
              scrollable
              scrollHeight="500px"
            >
              <Column field="BookingDetailID" header="ID" sortable />
              <Column field="BookingID" header="Invoice" sortable />
              <Column field="EmployeeID" header="Employee" body={employeeDropdownTemplate} sortable />
              <Column header="Date & Time" body={dateTimeTemplate} />
              <Column header="Service" body={combinedServiceTemplate} sortable />
              <Column header="Customer" body={customerTemplate} />
              <Column field="StatusName" header="Status" sortable />
            </DataTable>
          </OverlayPanel>

          <div className="header-icons">
            <i className="fas fa-solid fa-sliders"></i>
            <Dropdown
              value={language}
              options={languageOptions}
              onChange={handleLanguageChange}
              placeholder="Select Language"
              className="language-dropdown"
              optionLabel="label"
              valueTemplate={languageDropdownTemplate}
              itemTemplate={languageDropdownTemplate}
            />
          </div>
        </div>
      </header>

      <Menubar model={getMenubarItems()} />
    </>
  );
};

export default Header;
